import type { CSSProperties } from 'react';
import { STAGE_MODES, type StageMode } from '../model/vehicleDriving';
import { buildClassName } from './buildClassName';

interface StageModeEntry {
  mode: StageMode;
  label: string;
}

const STAGE_MODE_ENTRIES: StageModeEntry[] = [
  { mode: STAGE_MODES.VIEWING, label: '보기' },
  { mode: STAGE_MODES.DRIVING, label: '주행' },
];

export interface StageModeToggleProps {
  stageMode: StageMode;
  onStageModeChange: (mode: StageMode) => void;
}

/**
 * 보기 모드와 주행 모드를 가르는 세그먼트.
 *
 * 패널이 아니라 뷰어 위에 둡니다. 두 모드는 3D 조작 방식 자체를 바꾸는 것이라
 * (보기: 궤도 회전 / 주행: 시점 고정하고 차를 조작) 차량 사양 옵션과 같은 자리에 있으면
 * 성격이 섞입니다. 게다가 주행 모드에서는 그 패널이 잠기므로, 잠긴 패널 안에 잠금을 푸는
 * 스위치가 있는 모양이 됩니다.
 *
 * 생김새는 OptionPanel.css의 보기 모드 세그먼트와 같은 언어를 쓰지만 클래스를
 * 공유하지 않습니다. 이쪽은 3D 위에 뜨는 어두운 글래스라 색과 배경이 처음부터
 * 다르고, 패널 규칙을 상속받으면 덮어쓸 것이 더 많습니다.
 */
export function StageModeToggle({ stageMode, onStageModeChange }: StageModeToggleProps) {
  const selectedIndex = STAGE_MODE_ENTRIES.findIndex((entry) => entry.mode === stageMode);

  return (
    <div className="stage-mode-toggle" role="group" aria-label="쇼룸 모드">
      <span
        className="stage-mode-toggle__thumb"
        aria-hidden="true"
        // 커스텀 프로퍼티는 CSSProperties에 없어서 여기서 한 번 좁혀 줍니다.
        style={{ '--tds-segment-index': Math.max(selectedIndex, 0) } as CSSProperties}
      />
      {STAGE_MODE_ENTRIES.map((entry) => {
        const isSelected = entry.mode === stageMode;
        return (
          <button
            key={entry.mode}
            type="button"
            className={buildClassName('stage-mode-toggle__button', 'stage-mode-toggle__button--selected', isSelected)}
            aria-pressed={isSelected}
            onClick={() => onStageModeChange(entry.mode)}
          >
            {entry.label}
          </button>
        );
      })}
    </div>
  );
}
